// src/pages/SettingsPage/SettingsPage.tsx

import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Avatar,
  Stack,
  Snackbar,
  Alert,
  MenuItem,
  Chip,
} from "@mui/material";
import { motion } from "framer-motion";
import { useAuth } from "../../context/AuthContext";

const SettingsPage: React.FC = () => {
  const { user, updateRole, updateAvatarURL } = useAuth();

  const [role, setRole] = useState(user?.role || "Admin");
  const [preview, setPreview] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info";
  }>({ open: false, message: "", severity: "success" });

  /**
   * Safely converts ANY backend URL → relative path.
   */
  const toRelative = (raw: string | undefined | null): string | null => {
    if (!raw) return null;

    try {
      const absolute = new URL(raw, window.location.origin);
      return absolute.pathname;
    } catch {
      return raw.startsWith("/") ? raw : null;
    }
  };

  const handleAvatarUpload = async (file: File) => {
    if (!user) {
      return setSnackbar({
        open: true,
        message: "User not logged in",
        severity: "error",
      });
    }

    const previewUrl = URL.createObjectURL(file);
    setPreview(previewUrl);

    const formData = new FormData();
    formData.append("avatar", file);

    try {
      const response = await fetch(`/api/avatar/${user._id}`, {
        method: "POST",
        body: formData,
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.message || `Upload failed (${response.status})`);
      }

      const rawUrl = data.avatarUrl || data.avatarFull || data.avatar;
      const relativePath = toRelative(rawUrl);

      if (!relativePath) {
        throw new Error("Invalid avatar URL returned by server.");
      }

      updateAvatarURL(relativePath);

      setSnackbar({
        open: true,
        message: "Avatar updated successfully!",
        severity: "success",
      });
    } catch (err: any) {
      console.error("Avatar upload error:", err);
      setSnackbar({
        open: true,
        message: err.message || "Failed to upload avatar",
        severity: "error",
      });
    } finally {
      URL.revokeObjectURL(previewUrl);
      setPreview(null);
    }
  };

  const handleSaveRole = () => {
    updateRole(role);
    setSnackbar({
      open: true,
      message: "Role updated successfully",
      severity: "success",
    });
  };

  return (
    <Box
      sx={{
        p: { xs: 2, md: 4 },
        backgroundColor: "#1B2431",
        minHeight: "100vh",
      }}
    >
      <Typography variant="h4" fontWeight={700} mb={5} sx={{ color: "#FFFFFF" }}>
        Settings
      </Typography>

      <Stack direction={{ xs: "column", md: "row" }} spacing={5}>
        {/* AVATAR SETTINGS */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Card
            sx={{
              backgroundColor: "#273142",
              boxShadow: "0 10px 30px rgba(0,0,0,0.4)",
            }}
          >
            <CardContent sx={{ textAlign: "center", py: 6, px: 4 }}>
              <Avatar
                src={preview || user?.avatar || undefined}
                alt={user?.email}
                sx={{
                  width: 160,
                  height: 160,
                  mx: "auto",
                  mb: 3,
                  border: 4,
                  borderColor: "#4880FF",
                  boxShadow: "0 0 20px rgba(72, 128, 255, 0.3)",
                }}
              />

              <Button
                variant="contained"
                component="label"
                size="large"
                sx={{
                  backgroundColor: "#323D4E",
                  color: "white",
                  borderRadius: 3,
                  px: 4,
                  py: 1.5,
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": {
                    backgroundColor: "#3A4659",
                  },
                }}
              >
                Change Avatar
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={(e) =>
                    e.target.files?.[0] && handleAvatarUpload(e.target.files[0])
                  }
                />
              </Button>

              <Box mt={5}>
                <Typography variant="subtitle2" color="#91969E" gutterBottom>
                  Logged in as
                </Typography>
                <Typography variant="h6" fontWeight={600} sx={{ color: "#FFFFFF" }}>
                  {user?.email}
                </Typography>
                <Chip
                  label={user?.role || "User"}
                  sx={{
                    mt: 2,
                    fontWeight: "bold",
                    backgroundColor: "#4880FF",
                    color: "white",
                    border: "none",
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </motion.div>

        {/* ACCOUNT SETTINGS */}
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          style={{ flex: 2 }}
        >
          <Card
            sx={{
              backgroundColor: "#273142",
              boxShadow: "0 10px 30px rgba(0,0,0,0.4)",
            }}
          >
            <CardContent sx={{ p: 5 }}>
              <Typography variant="h5" fontWeight={700} mb={4} sx={{ color: "#FFFFFF" }}>
                Account Preferences
              </Typography>

              <Stack spacing={4}>
                <TextField
                  label="Email Address"
                  value={user?.email || ""}
                  fullWidth
                  InputProps={{ readOnly: true }}
                  variant="outlined"
                  sx={{
                    "& .MuiInputLabel-root": { color: "#91969E" },
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#323D4E",
                      color: "#E5E7EB",
                      "& fieldset": { borderColor: "#4B5563" },
                      "&:hover fieldset": { borderColor: "#5E6B7F" },
                    },
                  }}
                />

                <TextField
                  select
                  label="Role"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  fullWidth
                  variant="outlined"
                  sx={{
                    "& .MuiInputLabel-root": { color: "#91969E" },
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#323D4E",
                      color: "#E5E7EB",
                      "& fieldset": { borderColor: "#4B5563" },
                      "&:hover fieldset": { borderColor: "#5E6B7F" },
                    },
                  }}
                >
                  <MenuItem value="Admin">Admin</MenuItem>
                  <MenuItem value="User">User</MenuItem>
                  <MenuItem value="Health Worker">Health Worker</MenuItem>
                  <MenuItem value="Lab Technician">Lab Technician</MenuItem>
                </TextField>

                <Button
                  variant="contained"
                  size="large"
                  onClick={handleSaveRole}
                  sx={{
                    alignSelf: "flex-start",
                    backgroundColor: "#323D4E",
                    color: "white",
                    px: 5,
                    py: 1.5,
                    borderRadius: 3,
                    textTransform: "none",
                    fontWeight: 600,
                    "&:hover": {
                      backgroundColor: "#3A4659",
                    },
                  }}
                >
                  Save Role
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </motion.div>
      </Stack>

      {/* SNACKBAR */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%", fontSize: "1rem" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SettingsPage;